import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, List, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/MicOutlined';
import StopIcon from '@mui/icons-material/StopOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { aiEdit, fetchAiStatus, suggestShorts, type AiStatus, type AiTurn, type ShortSuggestion } from '../../services/editingApi';
import type { EditList } from '../../utils/editing/editList';
import { debugLog } from '../../utils/debugLog';
import { formatMs, keepOnly, previewOutputMs } from './editStudioModel';
import { brand } from '../../theme';

interface CommandBarProps {
  editId: string;
  list: EditList;
  /** Applies a proposed list to the undo history (the studio autosaves it). */
  onApply: (next: EditList, summary: string) => void;
  onUndo: () => void;
  /** For tests: how long a recording may run. */
  maxRecordMs?: number;
}

const EXAMPLES = ['Trim the first 12 seconds and add my logo top right', 'Make a 45-second short about the best part, with captions', 'Put my name at the start for three seconds', 'Add music under the speech'];

const toBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

/** Text or voice in, a changed edit out, always undoable; Shorts suggested from the transcript. */
const CommandBar: React.FC<CommandBarProps> = ({ editId, list, onApply, onUndo, maxRecordMs = 30000 }) => {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'asking' | 'listening' | 'shorts' | null>(null);
  const [reply, setReply] = useState<{ text: string; changed: boolean; instruction: string } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [history, setHistory] = useState<AiTurn[]>([]);
  const [shorts, setShorts] = useState<ShortSuggestion[] | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const listRef = useRef(list);
  listRef.current = list;

  useEffect(() => {
    let cancelled = false;
    fetchAiStatus().then((s) => { if (!cancelled) setStatus(s); }).catch((error) => debugLog('AI status failed', error));
    return () => { cancelled = true; };
  }, []);

  const ask = useCallback(async (input: { instruction?: string; audioBase64?: string; audioContentType?: string }) => {
    setBusy('asking');
    setProblem(null);
    try {
      const result = await aiEdit(editId, { ...input, history });
      setReply({ text: result.reply, changed: result.changed, instruction: result.instruction });
      setHistory((h) => [...h.slice(-5), { instruction: result.instruction, reply: result.reply }]);
      setStatus((s) => (s ? { ...s, promptsUsed: s.promptsPerMonth - result.promptsLeft } : s));
      if (result.changed) onApply(result.list, result.instruction);
      setText('');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The AI couldn’t answer. Try again.');
    } finally {
      setBusy(null);
    }
  }, [editId, history, onApply]);

  const send = () => { if (text.trim()) ask({ instruction: text.trim() }); };

  const startListening = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || typeof MediaRecorder === 'undefined') {
      setProblem('Your browser can’t record here. Type the instruction instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((type) => MediaRecorder.isTypeSupported(type)) || '';
      const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunks.current = [];
      mediaRecorder.ondataavailable = (event) => { if (event.data.size > 0) chunks.current.push(event.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        if (blob.size === 0) {
          setBusy(null);
          setProblem('Nothing was recorded. Try again.');
          return;
        }
        await ask({ audioBase64: await toBase64(blob), audioContentType: blob.type });
      };
      recorder.current = mediaRecorder;
      mediaRecorder.start();
      setBusy('listening');
      setProblem(null);
      window.setTimeout(() => { if (recorder.current === mediaRecorder && mediaRecorder.state === 'recording') mediaRecorder.stop(); }, maxRecordMs);
    } catch (error) {
      debugLog('Microphone not available', error);
      setProblem('The microphone couldn’t be used. Allow it in your browser, or type the instruction.');
    }
  };

  const stopListening = () => {
    if (recorder.current && recorder.current.state === 'recording') recorder.current.stop();
  };

  const findShorts = async () => {
    setBusy('shorts');
    setProblem(null);
    try {
      const result = await suggestShorts(editId, { count: 3, targetSeconds: 45 });
      setShorts(result.shorts);
      setStatus((s) => (s ? { ...s, promptsUsed: s.promptsPerMonth - result.promptsLeft } : s));
      if (result.shorts.length === 0) setProblem('No good stretches were found. Shorts work best on a few minutes of talk; for a short clip, mark the stretch yourself on the Cut tab.');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Shorts couldn’t be suggested.');
    } finally {
      setBusy(null);
    }
  };

  const applyShort = (short: ShortSuggestion) => {
    const current = listRef.current;
    const fromOut = previewOutputMs(current, short.fromMs);
    const toOut = previewOutputMs(current, Math.max(short.fromMs, short.toMs - 1));
    if (fromOut === null || toOut === null) {
      setProblem('That stretch was cut from this edit already. Undo the cut first, or pick another.');
      return;
    }
    const next = keepOnly(current, fromOut, toOut + 1);
    onApply({ ...next, output: { ...next.output, aspect: '9:16' }, reframe: next.reframe || { focusXPct: 50, focusYPct: 45, mode: 'crop' } }, `Short: ${short.title}`);
    setShorts(null);
    setReply({ text: `Kept ${formatMs(short.fromMs)} – ${formatMs(short.toMs)} as a 9:16 Short: “${short.title}”. Add captions in the Captions tab if you like.`, changed: true, instruction: `Short: ${short.title}` });
  };

  if (status && !status.available) {
    return (
      <Alert severity="info" icon={<AutoAwesomeIcon />} sx={{ mb: 2 }}>
        {status.promptsPerMonth === 0 ? 'The AI assistant comes with Pro and Pro Max: tell it what to change, in your words.' : 'The AI assistant isn’t available right now.'}
      </Alert>
    );
  }

  const left = status ? Math.max(0, status.promptsPerMonth - status.promptsUsed) : null;

  return (
    <Box sx={{ mb: 2, p: 1.5, border: `1px solid ${brand.border}`, borderRadius: 3, bgcolor: 'background.paper' }} data-testid="command-bar">
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoAwesomeIcon fontSize="small" sx={{ color: brand.copper }} />
        <TextField
          size="small"
          fullWidth
          placeholder="Tell the assistant what to change…"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }}
          disabled={busy !== null}
          inputProps={{ 'aria-label': 'Instruction for the assistant', maxLength: 1000, 'data-testid': 'command-input' }}
        />
        {status?.speech ? (
          busy === 'listening' ? (
            <Tooltip title="Stop and send"><IconButton color="error" onClick={stopListening} aria-label="Stop recording"><StopIcon /></IconButton></Tooltip>
          ) : (
            <Tooltip title="Say it"><span><IconButton onClick={startListening} disabled={busy !== null} aria-label="Speak an instruction"><MicIcon /></IconButton></span></Tooltip>
          )
        ) : null}
        <Button variant="contained" onClick={send} disabled={busy !== null || !text.trim()} endIcon={busy === 'asking' ? <CircularProgress size={14} color="inherit" /> : <SendIcon />} data-testid="command-send">
          {busy === 'asking' ? 'Thinking' : 'Do it'}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Button size="small" onClick={findShorts} disabled={busy !== null} data-testid="find-shorts">{busy === 'shorts' ? 'Looking…' : 'Suggest Shorts'}</Button>
        {left !== null ? <Typography variant="caption" color="text.secondary">{left} of {status?.promptsPerMonth} prompts left this month</Typography> : null}
        {status?.offline ? <Chip size="small" variant="outlined" label="Built-in helper (no AI key)" /> : null}
        {busy === 'listening' ? <Chip size="small" color="error" label="Listening… tap stop when you’re done" /> : null}
      </Stack>
      {!reply && !problem && !shorts ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Try: {EXAMPLES.map((example, index) => (
            <React.Fragment key={example}>
              {index > 0 ? ' · ' : ''}
              <Box component="button" type="button" onClick={() => setText(example)} sx={{ border: 0, p: 0, bgcolor: 'transparent', color: brand.teal, cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}>{example}</Box>
            </React.Fragment>
          ))}
        </Typography>
      ) : null}
      {problem ? <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setProblem(null)}>{problem}</Alert> : null}
      {reply ? (
        <Alert
          severity={reply.changed ? 'success' : 'info'}
          sx={{ mt: 1 }}
          data-testid="command-reply"
          action={reply.changed ? <Button color="inherit" size="small" onClick={() => { onUndo(); setReply(null); }}>Undo</Button> : undefined}
          onClose={() => setReply(null)}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>“{reply.instruction}”</Typography>
          {reply.text}{reply.changed ? ' Check the preview; nothing is exported until you say so.' : ''}
        </Alert>
      ) : null}
      {shorts && shorts.length > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Shorts worth cutting</Typography>
          <List dense disablePadding>
            {shorts.map((short) => (
              <ListItemButton key={`${short.fromMs}-${short.toMs}`} onClick={() => applyShort(short)} sx={{ borderRadius: 1 }} data-testid="short-suggestion">
                <ListItemText primary={`${short.title} · ${formatMs(short.fromMs)} – ${formatMs(short.toMs)}`} secondary={short.reason} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            ))}
          </List>
          <Button size="small" onClick={() => setShorts(null)}>Dismiss</Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default CommandBar;
